import { useState } from "react";
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { T, SERIF } from "../lib/model";

const { FiX, FiCheckCircle } = FiIcons;

export default function LeadGate({ onUnlock, onClose, saving }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", headcount: "", role: "" });
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setErr("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setErr("Please enter a valid work email.");
    if (!form.company.trim()) return setErr("Please enter your company name.");
    if (!form.headcount) return setErr("Please select company size.");
    if (!form.role.trim()) return setErr("Please enter your role.");
    setErr("");
    onUnlock({ 
      name: form.name.trim(), 
      email: form.email.trim(), 
      company: form.company.trim(),
      headcount: form.headcount,
      role: form.role.trim()
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md" style={{ background: "rgba(15,26,43,0.85)" }}>
      <div className="w-full max-w-xl bg-white rounded-[40px] overflow-hidden shadow-2xl relative animate-in fade-in zoom-in duration-300">
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <SafeIcon icon={FiX} className="w-6 h-6 text-gray-400" />
        </button>

        <div className="grid md:grid-cols-[200px_1fr]">
          <div className="bg-[#0D2440] p-8 hidden md:flex flex-col justify-between">
            <div className="space-y-6">
              <div style={{ color: T.lime, fontSize: 10, letterSpacing: "0.2em", fontWeight: 800 }}>EXECUTIVE SUMMARY</div>
              <ul className="space-y-4">
                {["Full Score Breakdown", "Top 3 Risk Drivers", "90-Day Roadmap"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-blue-100/70 text-xs font-bold">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-lime-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <img
              src="https://media-manager-c.questera.ai/greta-media/ecb16a7902f793d76e4d541ba9ce1c3ae6d96976df461599e59c7024cc2713617bff44ec4f8cbf96d32b4bb87bd2d993/images/aW1hZ2UvcG5n/07cb85c4268f1317cf05349009b1d943.png"
              alt="Logo"
              style={{ height: 24, width: "auto" }}
              className="opacity-50 grayscale"
            />
          </div>

          <div className="p-10">
            <h2 style={{ fontFamily: SERIF, color: T.ink, fontSize: 32 }} className="font-black leading-tight">
              Get Your Human Risk Report
            </h2>
            <p className="text-gray-500 mt-4 font-medium leading-relaxed">
              Get a one-page executive summary with your risk score, exposure drivers, and recommended next steps.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Full Name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Smith"
                    className="w-full rounded-2xl px-5 py-4 text-sm font-bold border border-gray-100 bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Work Email</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@company.com"
                    className="w-full rounded-2xl px-5 py-4 text-sm font-bold border border-gray-100 bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Company Name</span>
                  <input
                    type="text"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Acme Corp"
                    className="w-full rounded-2xl px-5 py-4 text-sm font-bold border border-gray-100 bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Job Role</span>
                  <input
                    type="text"
                    value={form.role}
                    onChange={set("role")}
                    placeholder="CEO / IT Manager"
                    className="w-full rounded-2xl px-5 py-4 text-sm font-bold border border-gray-100 bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Employee Count</span>
                <select
                  value={form.headcount}
                  onChange={set("headcount")}
                  className="w-full rounded-2xl px-5 py-4 text-sm font-bold border border-gray-100 bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all appearance-none"
                >
                  <option value="">Select size...</option>
                  <option value="1-20">1-20 Employees</option>
                  <option value="21-100">21-100 Employees</option>
                  <option value="101-500">101-500 Employees</option>
                  <option value="501+">501+ Employees</option>
                </select>
              </div>

              {err && (
                <div className="flex items-center gap-2 p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold animate-in slide-in-from-top-2">
                  <SafeIcon icon={FiIcons.FiAlertCircle} className="w-4 h-4" />
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full py-5 rounded-2xl font-black text-lg transition-all hover:brightness-105 disabled:opacity-50 shadow-xl shadow-lime-900/10 active:scale-95"
                style={{ background: T.lime, color: T.ink }}
              >
                {saving ? "Processing..." : "Download My Human Risk Report"}
              </button>

              <p className="text-[10px] text-center text-gray-400 font-bold uppercase tracking-wider mt-4">
                Privacy Guaranteed &middot; No Spam &middot; vCISO Standard
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}